import cn from "classnames";
import type React from "react";
import { useEffect, useState } from "react";
import { GiCheckMark } from "react-icons/gi";
import { useActivityCompletion } from "../../../hooks/useActivitiesCompletion";
import { Fireworks } from "../../../shared/components/Fireworks/Fireworks";
import style from "./MatchWordsWithPicturesMobile.module.scss";

type MatchItem = {
	imageUrl: string;
	word: string;
};

type MatchedPair = {
	image: MatchItem;
	word: MatchItem;
	matched: boolean;
};

type Props = {
	activity: { word: string; imageUrl: string }[];
	activityId: string;
};

const shuffleArray = <T,>(array: T[]): T[] => {
	return [...array].sort(() => Math.random() - 0.5);
};

export const MatchWordsWithPicturesMobile: React.FC<Props> = ({
	activity,
	activityId,
}) => {
	const [shuffledImages, setShuffledImages] = useState<MatchItem[]>([]);
	const [shuffledWords, setShuffledWords] = useState<MatchItem[]>([]);
	const [selectedImage, setSelectedImage] = useState<MatchItem | null>(null);
	const [selectedWord, setSelectedWord] = useState<MatchItem | null>(null);
	const [matchedPairs, setMatchedPairs] = useState<MatchedPair[]>([]);
	const { triggerActivityCompletion } = useActivityCompletion();
	const [fireworksTrigger, setFireworksTrigger] = useState(false);

	useEffect(() => {
		const shuffledImages = shuffleArray(activity);
		const shuffledWords = shuffleArray(activity);
		setShuffledImages(shuffledImages);
		setShuffledWords(shuffledWords);
	}, [activity]);

	const handleImageClick = (image: MatchItem) => {
		setSelectedImage(image);
		if (selectedWord && selectedWord.word === image.word) {
			const newMatchedPairs = [
				...matchedPairs,
				{
					image,
					word: selectedWord,
					matched: image.word === selectedWord.word,
				},
			];
			setMatchedPairs(newMatchedPairs);
			setSelectedImage(null);
			setSelectedWord(null);
			checkCompletion(newMatchedPairs);
		}
	};

	const handleWordClick = (word: MatchItem) => {
		setSelectedWord(word);
		if (selectedImage && selectedImage.word === word.word) {
			const newMatchedPairs = [
				...matchedPairs,
				{
					image: selectedImage,
					word,
					matched: selectedImage.word === word.word,
				},
			];
			setMatchedPairs(newMatchedPairs);
			setSelectedImage(null);
			setSelectedWord(null);
			checkCompletion(newMatchedPairs);
		}
	};

	const resetActivity = () => {
		setSelectedImage(null);
		setSelectedWord(null);
		setMatchedPairs([]);
		setFireworksTrigger(false);
		setShuffledImages(shuffleArray(activity));
		setShuffledWords(shuffleArray(activity));
	};

	const checkCompletion = (newMatchedPairs: MatchedPair[]) => {
		if (newMatchedPairs.length === activity?.length) {
			triggerActivityCompletion(activityId, 16, resetActivity);
			setFireworksTrigger(true);
		}
	};

	const remainingImages = shuffledImages.filter(
		(image) =>
			!matchedPairs.some((pair) => pair.image.imageUrl === image.imageUrl),
	);

	const remainingWords = shuffledWords.filter(
		(word) => !matchedPairs.some((pair) => pair.word.word === word.word),
	);

	return (
		<div className={style.matchActivityContainer}>
			{fireworksTrigger && (
				<Fireworks
					trigger={fireworksTrigger}
					onComplete={() => setFireworksTrigger(false)}
				/>
			)}
			<div className={style.pairsContainer}>
				<div className={style.imagesColumn}>
					{remainingImages.map((image) => (
						<div
							key={image.imageUrl}
							className={`${style.imageItem} ${
								selectedImage && selectedImage.imageUrl === image.imageUrl
									? style.selected
									: ""
							}`}
							onClick={() => handleImageClick(image)}
							onKeyUp={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									handleImageClick(image);
								}
							}}
						>
							<img src={image.imageUrl} alt={image.word} />
						</div>
					))}
				</div>
				<div className={style.wordsColumn}>
					{remainingWords.map((word) => (
						<div
							key={word.word}
							className={`${style.wordItem} ${
								selectedWord && selectedWord.word === word.word
									? style.selected
									: ""
							}`}
							onClick={() => handleWordClick(word)}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									handleWordClick(word);
								}
							}}
						>
							{word.word}
						</div>
					))}
				</div>
			</div>

			<div className={style.matchedPairsContainer}>
				{matchedPairs.map((pair) => {
					return (
						<div key={pair.word.word} className={cn(style.matchedItem)}>
							<div
								className={cn(style.imageItem, {
									[style.matched]: pair.matched,
								})}
							>
								<img src={pair.image.imageUrl} alt={pair.word.word} />
							</div>
							<GiCheckMark />
							<div
								className={cn(style.wordItem, {
									[style.matched]: pair.matched,
								})}
							>
								{pair.word.word}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};
